// React Dependencies
import React, { useState, useEffect, useReducer } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FormControlLabel, Radio, RadioGroup, TextField } from '@material-ui/core';

// Internal Dependencies
import apiService from '../../../services.js/apiService';
import OverLoading from '../../global/OverLoading';
import MText from '../../common/MText';
import MButton from '../../common/MButton';
import Colours from '../../../styles/colours';
import { Status } from '../../../common/constants';

const defaultInputs = {
  name:        '',
  description: '',
  status:      Status.Draft,
};

const AlbumInfo = ({ match }) => {
  const { pageId, id: albumId } = match.params;

  const [isLoading, setIsLoading] = useState(false);
  const [album, setAlbum] = useState({});
  const [inputs, setInputs] = useReducer((state, newState) => ({ ...state, ...newState }), defaultInputs);

  const isRejected = album.status === Status.Rejected;
  const isDraft = album.status === Status.Draft;
  const isLive = album.status === Status.Live;

  const checkStatusTransitionDisabled = (status) => {
    if (isLive) {
      return status === Status.Draft;
    } else if (isDraft) {
      return status === Status.Rejected;
    } else if (isRejected) {
      return [Status.Live, Status.Draft].includes(status);
    }
    return false;
  };

  const getAlbumData = async () => {
    try {
      setIsLoading(true);

      const fetchedAlbum = await apiService.getPageAlbum(pageId, albumId);

      setAlbum(fetchedAlbum);
      setInputs({
        name:        fetchedAlbum.name,
        description: fetchedAlbum.description,
        status:      fetchedAlbum.status,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const updatedAlbum = await apiService.updatePageAlbum(pageId, albumId, inputs);

      toast.success('Album updated successfully');
      setAlbum((prev) => ({
        ...prev,
        name:        updatedAlbum.name,
        description: updatedAlbum.description,
        status:      updatedAlbum.status,
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setInputs({ [name]: value });
  };

  useEffect(() => {
    getAlbumData();
  }, [pageId, albumId]);

  return (
    <>
      {isLoading && <OverLoading />}
      <div className="container-fluid pl-0">
        <div className="row">
          <div className="col-md-12">
            <MText bold size={20} text="General Information" />
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-md-12 col-xl-6">
            <MText text="Name" />
            <TextField
              fullWidth
              disabled={isRejected}
              name="name"
              placeholder="Name"
              variant="outlined"
              inputProps={{ style: { fontSize: 13 } }}
              size="small"
              value={inputs.name}
              onChange={handleInput}
            />
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-md-12 col-xl-6">
            <MText text="Description" />
            <TextField
              fullWidth
              disabled={isRejected}
              multiline
              rows={5}
              name="description"
              placeholder="Description"
              variant="outlined"
              inputProps={{ style: { fontSize: 13 } }}
              size="small"
              value={inputs.description}
              onChange={handleInput}
            />
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-md-12 col-xl-6">
            <MText text="Status" />
            <RadioGroup
              row
              name="status"
              value={inputs.status}
              onChange={handleInput}
              defaultValue={defaultInputs.status}
            >
              {[Status.Draft, Status.Live, Status.Rejected].map((status) => (
                <div className="mr-3">
                  <FormControlLabel
                    value={status}
                    control={<Radio color="secondary" disabled={checkStatusTransitionDisabled(status)} />}
                    style={{ fontSize: 13 }}
                    label={status}
                  />
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-md-12 col-xl-6 d-flex justify-content-center">
            <MButton
              disabled={isRejected}
              colour={isRejected ? Colours.Gray1 : Colours.Primary}
              textColour={Colours.White}
              className="w-75"
              onClick={handleSave}
              text="Save"
            />
          </div>
        </div>
      </div>
    </>
  );
};

AlbumInfo.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape(Object).isRequired,
  }).isRequired,
};

export default AlbumInfo;
